import cx from 'classix';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import {
  type MouseEvent,
  useEffect,
  useState,
} from 'react';
import { BsArrowLeft } from 'react-icons/bs';
import { FaHeart } from 'react-icons/fa6';
import {
  useIsMounted,
  useScrollLock,
} from 'usehooks-ts';

import {
  NextButton,
  PrevButton,
  useCarouselArrows,
} from '@/features/listing/components/listing-card/listing-carousel-arrows/use-carousel-arrows';
import {
  SelectedSnapDisplay,
  useSelectedSnapDisplay,
} from '@/features/listing/components/listing-detailed/listing-detail-gallery/listing-detail-carousel-counter/listing-detail-carousel-counter';
// import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { type ListingDetailGalleryProps } from '@/features/listing/components/listing-detailed/listing-detail-gallery/listing-detail-gallery';

import styles from './listing-detail-all-photos.module.scss';

interface ListingDetailAllPhotosProps extends Omit<ListingDetailGalleryProps, 'listingType'> {
  onClose: () => void;
  onBookmarkClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

export default function ListingDetailAllPhotos(props: ListingDetailAllPhotosProps) {
  const {
    alt,
    images,
    isBookmarked,
    onBookmarkClick,
    onClose,
  } = props;

  const isMounted = useIsMounted();

  const [isPhotosCarouselShow,
    setIsPhotosCarouselShow] = useState(false);

  const [emblaRef,
    emblaApi] = useEmblaCarousel({ align: 'start' });

  const {
    nextBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
    prevBtnDisabled,
  } = useCarouselArrows(emblaApi);

  const {
    selectedSnap,
    snapCount,
  } = useSelectedSnapDisplay(emblaApi);

  const {
    lock,
    unlock,
  } = useScrollLock({ autoLock: false });

  const handlePhotosCarouselShow = () => {
    setIsPhotosCarouselShow(true);
  };

  const addToImgUrlExtraParams = (source: string) => {
    const url = new URL(source);
    url.searchParams.set('auto', 'webp');

    return url.toString();
  };

  const handleModalBackIconClick = () => {
    if (isPhotosCarouselShow) {
      setIsPhotosCarouselShow(false);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    lock();

    return () => {
      unlock();
    };
  }, [isMounted, lock, unlock]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent): void => {
      switch (event.key) {
        case 'Escape': {
          onClose();
          break;
        }
        case 'ArrowRight': {
          onNextButtonClick(event);
          break;
        }
        case 'ArrowLeft': {
          onPrevButtonClick(event);
          break;
        }
        default: {
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  });

  return (
    <div
      className={cx(
        styles.listingDetailAllPhotos,
        isPhotosCarouselShow && styles.listingDetailAllPhotos__isPhotosCarouselShow,
      )}
    >
      <div className={styles.listingDetailAllPhotos_wrapper}>
        <div className={styles.listingDetailAllPhotos_header}>
          <button
            aria-label="close modal"
            className={cx(
              styles.listingDetailAllPhotos_backIcon,
            )}
            onClick={handleModalBackIconClick}
          >
            {/* eslint-disable-next-line canonical/prefer-react-lazy */}
            <BsArrowLeft />
          </button>

          {isPhotosCarouselShow && (
            <SelectedSnapDisplay
              selectedSnap={selectedSnap}
              snapCount={snapCount}
            />
          )}

          <button
            className={styles.listingDetailAllPhotos_bookmark}
            aria-label="favorite"
            onClick={onBookmarkClick}
          >
            <FaHeart
              className={cx(styles.listingDetailAllPhotos_bookmarkIcon, isBookmarked && styles.listingDetailAllPhotos_bookmarkIcon__isBookmarked)}
            />
          </button>
        </div>

        {/* <div className={styles.listingDetailAllPhotos_margin}> */}
        {/*   <ResponsiveMasonry */}
        {/*     columnsCountBreakPoints={{ 350: 1, 750: 2 }} */}
        {/*   > */}
        {/*     <Masonry columnsCount={2} gutter="10px"> */}
        {/*       {images.map((photo) => ( */}
        {/*         <img */}
        {/*           style={{ display: 'block', width: '100%' }} */}
        {/*           src={photo.path} */}
        {/*           alt={alt} */}
        {/*           key={photo.id} */}
        {/*         /> */}
        {/*       ))} */}
        {/*     </Masonry> */}
        {/*   </ResponsiveMasonry> */}
        {/* </div> */}

        {isPhotosCarouselShow ? (
          <section className={styles.embla}>
            <div className={styles.embla__viewport} ref={emblaRef}>
              <div className={styles.embla__container}>
                {images.map((image) => (
                  <div key={image.id} className={styles.embla__slide}>
                    <img
                      className={cx(styles.embla__slide__img)}
                      src={addToImgUrlExtraParams(image.path)}
                      alt={alt}
                      // width={372}
                      // height={279}
                      // quality={25}
                      // priority={index === 0}
                      // unoptimized
                      // // onLoad={setLoaded}
                      // data-src={src}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.embla__buttons}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} isListingDetailPreview />
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} isListingDetailPreview />
            </div>
          </section>
        ) : (
          <div className={styles.listingDetailAllPhotos_grid}>
            {images.map((photo) => (
              <button onClick={handlePhotosCarouselShow} key={photo.id} className={styles.listingDetailAllPhotos_item}>
                <Image
                  src={photo.path}
                  alt={alt}
                  // width={372}
                  // height={279}
                  quality={75}
                  fill
                  unoptimized
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
